/**
 * Print helper that uses `window.print()` directly on the current page —
 * no popups, so it works even when the user's browser blocks new windows.
 *
 * How it works:
 *   1. Inject a `<div class="print-portal">` into <body> with one
 *      `<section class="print-page">` per image.
 *   2. Inject a `<style>` with `@media print` rules that hide everything
 *      EXCEPT the portal, and force one image per page via
 *      `page-break-after: always`.
 *   3. Pre-load every image (`img.complete` or wait for `load`/`error`)
 *      so the print preview doesn't snapshot half-loaded pictures.
 *   4. Temporarily overwrite `document.title` so the default print
 *      header / saved PDF filename gets a meaningful name.
 *   5. Call `window.print()`. Restore the title + remove the portal +
 *      remove the style after the dialog is dismissed (via the native
 *      `afterprint` event when available, with a safety timer as
 *      a fallback for browsers that don't fire it reliably).
 */

const STYLE_ID = '__print-portal-style';
const PORTAL_CLASS = 'print-portal';

function buildStyle() {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    /* Off-screen on screen — only the print media query brings it on. */
    .${PORTAL_CLASS} {
      position: fixed;
      left: -100000px;
      top: 0;
      width: 0;
      height: 0;
      overflow: hidden;
      visibility: hidden;
    }

    @media print {
      @page { margin: 0.5in; }
      /* Hide the whole app, show only the portal. */
      html, body { background: #fff !important; }
      body > *:not(.${PORTAL_CLASS}) {
        display: none !important;
      }
      .${PORTAL_CLASS} {
        position: static !important;
        left: auto !important;
        width: auto !important;
        height: auto !important;
        overflow: visible !important;
        visibility: visible !important;
      }
      .${PORTAL_CLASS} .print-page {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 0.25in 0;
        min-height: 100vh;
        page-break-after: always;
        break-after: page;
      }
      .${PORTAL_CLASS} .print-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      .${PORTAL_CLASS} .caption {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 4px;
        text-align: center;
        color: #1f2933;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI",
                     Roboto, Helvetica, Arial, sans-serif;
      }
      .${PORTAL_CLASS} .subcap {
        font-size: 12px;
        color: #5b6773;
        margin: 0 0 12px;
        text-align: center;
        font-family: inherit;
      }
      .${PORTAL_CLASS} .img-wrap {
        flex: 1;
        min-height: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .${PORTAL_CLASS} .img-wrap img {
        max-width: 100%;
        max-height: 90vh;
        object-fit: contain;
      }
    }
  `;
  return el;
}

function buildPortal(pages) {
  const portal = document.createElement('div');
  portal.className = PORTAL_CLASS;

  pages.forEach((p) => {
    const section = document.createElement('section');
    section.className = 'print-page';

    if (p.caption) {
      const h2 = document.createElement('h2');
      h2.className = 'caption';
      h2.textContent = p.caption;
      section.appendChild(h2);
    }
    if (p.subcaption) {
      const sub = document.createElement('p');
      sub.className = 'subcap';
      sub.textContent = p.subcaption;
      section.appendChild(sub);
    }

    const wrap = document.createElement('div');
    wrap.className = 'img-wrap';
    const img = document.createElement('img');
    img.src = p.url;
    img.alt = p.caption || '';
    wrap.appendChild(img);
    section.appendChild(wrap);

    portal.appendChild(section);
  });

  return portal;
}

function waitForImages(portal) {
  const imgs = Array.from(portal.querySelectorAll('img'));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(imgs.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((res) => {
      const done = () => {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        res();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  }));
}

/**
 * @param {Array<{ url: string, caption?: string, subcaption?: string }>} pages
 * @param {string} [title='Print']  Used as the document title (drives the
 *   default "Save as PDF" filename in most browsers).
 */
export async function printImagePages(pages, title = 'Print') {
  if (!pages || pages.length === 0) return;

  // If something else is already printing, bail out gracefully.
  if (document.getElementById(STYLE_ID)) return;

  const style = buildStyle();
  const portal = buildPortal(pages);
  const originalTitle = document.title;

  document.head.appendChild(style);
  document.body.appendChild(portal);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    document.title = originalTitle;
    if (style.parentNode) style.parentNode.removeChild(style);
    if (portal.parentNode) portal.parentNode.removeChild(portal);
    window.removeEventListener('afterprint', cleanup);
  };

  try {
    await waitForImages(portal);
    document.title = title;
    // `afterprint` fires in every modern browser once the dialog closes
    // (whether the user actually printed or cancelled). A short-timer
    // fallback covers older WebKit which sometimes skips the event.
    window.addEventListener('afterprint', cleanup);
    setTimeout(cleanup, 60_000);
    window.print();
  } catch {
    cleanup();
  }
}
